/* eslint-disable @typescript-eslint/no-explicit-any */
import update from 'immutability-helper'
import { find } from 'lodash'

import { TicketList } from '../../common/models'
import {
  getTicketsByCategoryId as doGetTicketsByCategoryId,
  getCategory as doGetCategory,
} from '../../core/services/tickets'

// Actions
const START_TICKETS = 'tickets/getTicketsByCategoryId/START_TICKETS'
const COMPLETE_TICKETS = 'tickets/getTicketsByCategoryId/COMPLETE_TICKETS'
const FAIL_TICKETS = 'tickets/getTicketsByCategoryId/FAIL_TICKETS'

const START_CATEGORY = 'tickets/setTicketsCategory/START_CATEGORIES'
const COMPLETE_CATEGORY = 'tickets/setTicketsCategory/COMPLETE_CATEGORIES'
const FAIL_CATEGORY = 'tickets/getTicketsCategory/FAIL_CATEGORIES'

// Types
type TicketsByCategory = { [key: string]: TicketList[] }
type Category = {
  [key: string]: {
    id: string
    name: string
  }
}

type State = {
  isTicketsFailed: boolean
  isTicketsInProgress: boolean
  isTicketsDone: boolean
  ticketsByCategory: TicketsByCategory

  isCategoryFailed: boolean
  isCategoryInProgress: boolean
  isCategoryDone: boolean
  getTicketCategory: Category
}

type Action =
  | { type: typeof START_TICKETS }
  | {
      type: typeof COMPLETE_TICKETS
      categoryId: string
      ticketsList: TicketList[]
    }
  | { type: typeof FAIL_TICKETS }
  | { type: typeof START_CATEGORY }
  | {
      type: typeof COMPLETE_CATEGORY
      editionId: string
      id: string
      name: string
    }
  | { type: typeof FAIL_CATEGORY }

// Initial state
const ticketsList: TicketList[] = []
const getTicketCategory: Category = {}
const initialState: State = {
  isTicketsFailed: false,
  isTicketsInProgress: false,
  isTicketsDone: !!ticketsList,
  ticketsByCategory: {},
  isCategoryFailed: false,
  isCategoryInProgress: false,
  isCategoryDone: !!getTicketCategory,
  getTicketCategory: {},
}

// Reducer
export const reducer = (state = initialState, action: Action) => {
  switch (action.type) {
    case START_TICKETS:
      return update(state, {
        $merge: {
          isTicketsFailed: false,
          isTicketsInProgress: true,
          isTicketsDone: false,
        },
      })
    case COMPLETE_TICKETS:
      const obj: TicketsByCategory = state.ticketsByCategory
      obj[action.categoryId] = action.ticketsList
      return update(state, {
        $merge: {
          isTicketsFailed: false,
          isTicketsInProgress: false,
          isTicketsDone: true,
          ticketsByCategory: obj,
        },
      })
    case FAIL_TICKETS:
      return update(state, {
        $merge: {
          isTicketsFailed: true,
          isTicketsInProgress: false,
          isTicketsDone: false,
        },
      })
    case START_CATEGORY:
      return update(state, {
        $merge: {
          isCategoryFailed: false,
          isCategoryInProgress: true,
          isCategoryDone: false,
        },
      })
    case COMPLETE_CATEGORY:
      const category: Category = state.getTicketCategory
      category[action.editionId] = { id: action.id, name: action.name }
      return update(state, {
        $merge: {
          isCategoryFailed: false,
          isCategoryInProgress: false,
          isCategoryDone: true,
          getTicketCategory: category,
        },
      })
    case FAIL_CATEGORY:
      return update(state, {
        $merge: {
          isCategoryFailed: true,
          isCategoryInProgress: false,
          isCategoryDone: false,
        },
      })

    default:
      return state
  }
}

const startTickets = (): Action => ({
  type: START_TICKETS,
})

const completeTickets = (
  categoryId: string,
  ticketsList: TicketList[],
): Action => ({
  type: COMPLETE_TICKETS,
  categoryId,
  ticketsList,
})

const failTickets = (): Action => ({
  type: FAIL_TICKETS,
})

export const getTicketsFromRedux =
  (categoryId: string) => async (dispatch: any) => {
    dispatch(startTickets())
    try {
      const ticketsList = await doGetTicketsByCategoryId(categoryId, {}, false)
      dispatch(completeTickets(categoryId, ticketsList.data.content))
    } catch {
      return dispatch(failTickets())
    }
  }

const startCategory = (): Action => ({
  type: START_CATEGORY,
})

const completeCategory = (
  editionId: string,
  id: string,
  name: string,
): Action => ({
  type: COMPLETE_CATEGORY,
  editionId,
  id,
  name,
})

const failCategory = (): Action => ({
  type: FAIL_TICKETS,
})

export const setCategoryInRedux =
  (editionId: string, categoryId: string) => async (dispatch: any) => {
    dispatch(startCategory())
    try {
      const category = await doGetCategory(categoryId)
      const name = find(
        category.data.nameTranslations,
        (translation) => translation.locale === 'en',
      )?.name
      if (name) {
        dispatch(completeCategory(editionId, category.data.id, name))
      }
    } catch {
      return dispatch(failCategory())
    }
  }
